import { usePaginationQuery } from 'api'
import orderBy from 'lodash/orderBy'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { PropertyWorkOrderType, PropertyAlertType } from 'types/property.type'
import { useClient } from 'utils/auth-context'

export const PROPERTY_QUERY_KEY = 'properties'
export const ALL_PROPERTY_QUERY_KEY = 'all_properties'
export const PROPERTY_ENDPOINT = 'properties'
export const PROPERTY_DETAILS_KEY = "property-details"


export const useCreateProjectMutation = () => {
  const client = useClient()
  const queryClient = useQueryClient()

  return useMutation(
    (propertyPayload: any) => {
      return client('properties', {
        data: propertyPayload,
        method: 'POST',
      })
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(PROPERTY_QUERY_KEY)
        queryClient.invalidateQueries(PROPERTY_DETAILS_KEY)
      },
    },
  )
}

export const useUpdateProjectMutation = () => {
  const client = useClient()
  const queryClient = useQueryClient()

  return useMutation(
    (propertyPayload: any) => {
      return client('properties', {
        data: propertyPayload,
        method: 'PUT',
      })
    },
    {
      onSuccess( response ) {
        queryClient.invalidateQueries(["property", true, response?.data?.id?.toString()]);
        queryClient.invalidateQueries(PROPERTY_QUERY_KEY)
        queryClient.invalidateQueries(PROPERTY_DETAILS_KEY)
      },
    },
  )
}


export const useProperties = (filterQueryString?: string, page?: number, size: number = 0) => {
  const queryKey = [PROPERTY_QUERY_KEY, filterQueryString]
  const endPoint = `${PROPERTY_ENDPOINT}?${filterQueryString || ''}`

  const { data, ...rest } = usePaginationQuery<Array<any>>(queryKey, endPoint, size || 10, { enabled: size > 0 })

  return {
    properties: data?.data,
    totalPages: data?.totalCount,
    dataCount: data?.dataCount,
    ...rest,
  }
}

export const useGetAllProperties = (filterQueryString: string) => {
  const client = useClient()

  const { data, ...rest } = useQuery<Array<any>>(
    ALL_PROPERTY_QUERY_KEY,
    async () => {
      const response = await client(`${PROPERTY_ENDPOINT}?${filterQueryString}`, {})

      return response?.data
    },
    {
      enabled: false,
    },
  )

  return {
    allProjects: data,
    ...rest,
  }
}

export const useProperty = (propertyId?: string, fetchDetailClientInfo=false) => {
  const client = useClient()
  const clientDetailFetchQuery = fetchDetailClientInfo === true ? "?fetchClientInfo=true" : ""; // HN-PSWOA-5483| This query param will bring detail info of client of that property, use this param when required
  const queryKey = fetchDetailClientInfo ? ['property', fetchDetailClientInfo, propertyId] : ['property', propertyId];
  const { data: propertyData, ...rest } = useQuery<any>(queryKey, async () => {
  const response = await client(`${PROPERTY_ENDPOINT}/${propertyId}${clientDetailFetchQuery}`, {})

    return response?.data
  }, {
    enabled: !!propertyId
  })

  return {
    propertyData,
    ...rest,
  }
}

export const usePropertyWorkOrders = projectId => {
  const client = useClient()

  return useQuery<PropertyWorkOrderType[]>(['GetPropertyWorkOrders', projectId], async () => {
    const response = await client(`${PROPERTY_ENDPOINT}/${projectId}/workorders`, {})

    return orderBy(
      response?.data,
      [
        item => {
          const createdDate = new Date(item.createdDate)
          return createdDate
        },
      ],
      ['asc'],
    )
  })
}

export const usePropertyAlerts = (propertyId, login) => {
  const client = useClient('/alert/api')

  return useQuery<PropertyAlertType[]>('GetPropertyAlerts', async () => {
    const response = await client(`alert-histories/${propertyId}/${propertyId}`, {})

    return response?.data.filter(alert => alert.login === login)
  })
}

export const usePropertyNotes = ({ propertyId }: { propertyId: number | undefined }) => {
    const client = useClient()
  
    const { data: notes, ...rest } = useQuery<Array<Document>>(['notes', propertyId], async () => {
      const response = await client(`notes?projectId.equals=${propertyId}`, {})
      return response?.data
    })
  
    return {
      notes,
      ...rest,
    }
  }

  export const usePropertyTypes = () => {
    const client = useClient()
  
    return useQuery('propertyTypes', async () => {
      const response = await client(`project_type?page=&size=&sort=value,asc`, {})
  
      return response?.data
    })
  }